import './SupportPage.scss';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n';
import { useToast } from '../../components/Toast';
import { get, post } from '../../services/api';
import Card from '../../components/Card';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import EmptyState from '../../components/EmptyState';
import Skeleton from '../../components/Skeleton';
import type { Paginated } from '../../types';

type TabKey = 'my' | 'create';

interface Ticket {
  public_id: string;
  subject: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: string;
  assignee_name?: string;
  created_at: string;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
];

function priorityVariant(p: Ticket['priority']): 'neutral' | 'brand' | 'warning' | 'danger' {
  if (p === 'urgent') return 'danger';
  if (p === 'high') return 'warning';
  if (p === 'low') return 'neutral';
  return 'brand';
}

export default function SupportPage() {
  const { t } = useLang();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>('my');
  const [items, setItems] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const tabs = useMemo(() => ([
    { value: 'my', label: t('support.tab.my') },
    { value: 'create', label: t('support.tab.create') },
  ]), [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get<Paginated<Ticket>>('/me/tickets/');
        if (!cancelled) setItems(res.results || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setSubmitting(true);
    try {
      await post('/me/tickets/', { subject, body_md: body, priority, category });
      toast.success(t('profile.saved'));
      setSubject(''); setBody(''); setPriority('normal'); setCategory('general');
      setTab('my');
      const res = await get<Paginated<Ticket>>('/me/tickets/');
      setItems(res.results || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <h1 className="support-page__title">{t('support.title')}</h1>
      <Tabs value={tab} onChange={(v) => setTab(v as TabKey)} items={tabs} ariaLabel={t('support.title')} />
      {tab === 'my' ? (
        loading ? (
          <Skeleton height={200} />
        ) : items.length === 0 ? (
          <Card padding="lg"><EmptyState title={t('support.empty')} /></Card>
        ) : (
          <ul className="support-page__list">
            {items.map((tk) => (
              <li key={tk.public_id}>
                <Link to={`/cabinet/support/${tk.public_id}`} className="support-page__link">
                  <Card padding="md" className="support-page__item">
                    <div className="support-page__row">
                      <div className="support-page__info">
                        <div className="support-page__subject">{tk.subject}</div>
                        <div className="support-page__meta">
                          <span>{tk.assignee_name || '—'}</span>
                          <span>•</span>
                          <span>{new Date(tk.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="support-page__badges">
                        <Badge variant={priorityVariant(tk.priority)} size="sm">{tk.priority}</Badge>
                        <Badge variant="info" size="sm">{tk.status}</Badge>
                      </div>
                    </div>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : (
        <Card padding="lg">
          <form className="support-page__form" onSubmit={onCreate}>
            <Input label={t('support.subject')} value={subject} onChange={(e) => setSubject(e.target.value)} required />
            <Textarea label={t('support.body')} value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
            <div className="support-page__cols">
              <Select label={t('support.priority')} value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
              <Select label={t('support.category')} value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
            </div>
            <div>
              <Button type="submit" variant="primary" size="md" loading={submitting}>{t('support.submit')}</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
